import { generateRecurringDates } from "../../utils/functions/recurrence";
import React, { useEffect, useState } from "react";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useCreateEventAndRecurrentsMutation,
  useGetRecurrentEventsQuery,
  useUpdateEventHostMutation,
  useUpdateRecurrentEventsMutation,
  useGetOccupiedFeaturedOrdersQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useGetEventUsersQuery,
} from "../../store/services/events";
import { useGetUserByEmailQuery } from "../../store/services/users";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useSaveEventAttendanceConfigMutation } from "../../store/services/attendances";
import { AttendanceConfigDraft, getAttendanceConfigErrors } from "../../models/attendance";
import { useAttendanceConfigSave } from "../../hooks/useAttendanceConfigSave";
import { AttendanceConfigSection } from "./AttendanceConfigSection";
import { yupConstants } from "../../utils/constants/yup";
import { NewEventForm } from "../../models/forms";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { Panel } from "primereact/panel";
import { FormActions, FormSection, FormSections, useTenantGate } from "../ui";
import { EventBonusCardPreview } from "./EventBonusCardPreview";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Event, EventFetch } from "../../models/events";
import {
  guestsTypes,
  spacesTypes,
} from "../../utils/constants/forms-constants";
import { DefaultHost } from "../../utils/constants/anfitrion-grups";
import { InputFileCustom, ImageUrlsManager } from "..";
import { useGetEventTypesQuery } from "../../store/services/events-types";
import { buildRegistrationDeadlinePayload, mergeTimeIntoDate, parseDate } from "../../utils/functions/date";
import { RecurrenceFields } from "./ConcurrencyEventFrom";
import EventUpdateScopeDialog from "../Dialogs/EventUpdateScopeDialog";
import HostSpotUnavailableDialog from "../Dialogs/HostSpotUnavailableDialog";
import { createEventAndRecurrentsDTO } from "../../models/dtos/createEventAndRecurrentsDTO";
import { Checkbox } from "primereact/checkbox";
import { VenueSelector } from "../VenueSelector";
import { DeadlineHourPicker } from "./DeadlineHourPicker";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useGetBoMeQuery } from "../../store/services/api";
import { useGetVenueByIdQuery } from "../../store/services/venues";
import {
  buildEventBonusPayload,
  buildEventCtaPayload,
  changesEventBonus,
  EVENT_BONUS_CHECKINS_NOTICE,
  EVENT_BONUS_NOT_ENABLED_MESSAGE,
  MAX_EVENT_BONUS_POINTS,
  eventUpdateHasCheckins,
  exceedsVenueCapacity,
  findActiveEventBonusRule,
  hasSpotForParticipatingHost,
  isEventBonusNotEnabledError,
  resolveEventBonusMaxPoints,
  resolveEventFormActive,
  resolveEventFormBonusValues,
  resolveEventFormCtaValues,
  resolveOccupiedSpots,
} from "../../utils/functions/event-form";
import { formatPointRewardCost } from "../../models/pointReward";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useGetPointRulesQuery } from "../../store/services/point-rules";
import { useGetPointSettingsQuery } from "../../store/services/point-settings";
import { DEFAULT_POINT_CURRENCY_NAME } from "../../models/pointSettings";
import { useGetEventAdvancedConfigVisibilityRulesQuery } from "../../store/services/rules-event-advanced-config";
import { isEventNotificationSuppressionVisible } from "../../utils/eventAdvancedConfigVisibility";


interface EventFormProps {
  eventData?: Event;
  refetchEventData?: () => void;
}

const getFirstErrorMessage = (errorValue: unknown): string | null => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  if (Array.isArray(errorValue)) {
    for (const item of errorValue) {
      const nestedMessage = getFirstErrorMessage(item);
      if (nestedMessage) return nestedMessage;
    }
  }
  if (typeof errorValue === "object") {
    for (const value of Object.values(errorValue as Record<string, unknown>)) {
      const nestedMessage = getFirstErrorMessage(value);
      if (nestedMessage) return nestedMessage;
    }
  }
  return null;
};

export const EventForm = ({
  eventData = undefined,
  refetchEventData,
}: EventFormProps) => {
  const navigate = useNavigate();
  // const [userFullName, setuserFullName] = useState("");

  //use state for Event Update Scope Modal
  const [showUpdateScopeModal, setShowUpdateScopeModal] = useState(false);

  // Advertencia de cupo al intentar activar "Host participa" (GTK-717)
  const [showHostSpotDialog, setShowHostSpotDialog] = useState(false);

  // use state to set the form as pending while the user chooses update scope
  const [pendingFormData, setPendingFormData] = useState<EventFetch | null>(null);

  const [isAttendanceConfigReady, setIsAttendanceConfigReady] = useState(false);
  const [attendanceDraft, setAttendanceDraft] = useState<AttendanceConfigDraft | null>(null);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const handledSubmitCount = React.useRef(0);

  // Fetch recurrent events if eventData exists and has an id

  const {
    data: recurrentEvents,
    isLoading: isLoadingRecurrent,
  } = useGetRecurrentEventsQuery(eventData?.id || 0, {
    skip: !eventData?.id // Skip if no eventData 
  });

  const [updateRecurrentEvents, { isLoading: isLoadingRecurrentUpdate }] = useUpdateRecurrentEventsMutation();

  const { data: tenants } = useGetTenantsQuery({});
  const { data: boMe } = useGetBoMeQuery();
  const tenantScope = useTenantScope();
  const tenantGate = useTenantGate();
  const {
    data: advancedConfigVisibilityRules = [],
    isSuccess: isAdvancedConfigRulesLoaded,
  } = useGetEventAdvancedConfigVisibilityRulesQuery({});

  const resolveFormTenantId = React.useCallback(
    (current?: string | null) =>
      current || tenantScope.tenantId || boMe?.defaultTenantId || "",
    [tenantScope.tenantId, boMe?.defaultTenantId]
  );

  const tenantOptions = React.useMemo(() => (
    Array.isArray(tenants) ? tenants.map(tenant => ({
      ...tenant,
      displayName: `${tenant.provider || tenant.id} ${tenant.emailDomain ? `(${tenant.emailDomain})` : ''}`.trim()
    })) : []
  ), [tenants]);

  const getFeaturedTenantLabel = React.useCallback((tenantId?: string | null) => {
    if (!tenantId) return "este tenant";
    return tenantOptions.find((tenant) => tenant.id === tenantId)?.displayName || "este tenant";
  }, [tenantOptions]);

  const [createEvent, { isLoading: isLoadingCreate }] =
    useCreateEventMutation();

  const [updateEvent, { isLoading: isLoadingUpdate }] =
    useUpdateEventMutation();


  const [updateEventHost] =
    useUpdateEventHostMutation();

  const [saveEventAttendanceConfig, { isLoading: isSavingAttendanceConfig }] = useSaveEventAttendanceConfigMutation();

  const [createEventAndRecurrents, { isLoading: isLoadingRecurrentCreate }] = useCreateEventAndRecurrentsMutation();

  const entityId = eventData?.id != null ? String(eventData.id) : "";
  const { data: cachedEvent, isFetching: isEntityFetching } = useGetEventByIdQuery(
    entityId,
    { skip: !entityId }
  );
  const entity = cachedEvent ?? eventData;

  const goBack = () => navigate(-1);

  const now = new Date();
  const getGuests = (guests?: number) => {
    if (guests === 0) return "0";
    if (!!!guests) return "";
    return guests.toString();
  };

  const { data: defaultHost } = useGetUserByEmailQuery(DefaultHost.hostEmail);

  const occupiedFeaturedOrdersRef = React.useRef<
    { id: number; order: number; eventName: string }[] | undefined
  >(undefined);

  const getMutationErrorMessage = (error: any, fallback: string) => {
    if (isEventBonusNotEnabledError(error)) {
      return EVENT_BONUS_NOT_ENABLED_MESSAGE;
    }

    const message =
      error?.data?.error?.message ||
      error?.data?.errorMessage ||
      error?.data?.message ||
      error?.error;

    if (typeof message === "string" && message.trim()) {
      return message;
    }

    if (Array.isArray(message) && message.length > 0) {
      return message.join(". ");
    }

    return fallback;
  };

  /**
   * GTK-869 — Si la edición cambió el bonus y el backend avisa que el evento ya tenía
   * check-ins, se aclara que esos no se recalculan. El form vuelve atrás al guardar, así
   * que el aviso va en un toast y no en la sección.
   */
  const notifyBonusCheckins = (response: unknown, payload: EventFetch) => {
    if (changesEventBonus(entity, payload) && eventUpdateHasCheckins(response)) {
      toast(EVENT_BONUS_CHECKINS_NOTICE, { icon: "ℹ️" });
    }
  };

  const getActionErrorMessage = (action: string, fallbackReason: string, error: any) => {
    const reason = getMutationErrorMessage(error, fallbackReason);
    return `${action}: ${reason}`;
  };

  const shouldPersistAttendanceConfig = (draft: AttendanceConfigDraft | null): boolean =>
    draft !== null && (draft.enabled || draft.configId !== null);

  const persistAttendanceConfig = async (
    targetEventId: number | null | undefined,
    forceSave = false
  ): Promise<boolean> => {
    if (!targetEventId || !attendanceDraft || (!forceSave && !shouldPersistAttendanceConfig(attendanceDraft))) {
      return true;
    }

    try {
      await saveEventAttendanceConfig({
        eventId: targetEventId,
        enabled: attendanceDraft.enabled,
        method: attendanceDraft.method,
        attendanceHostUserId: attendanceDraft.attendanceHostUserId,
        maxAttendees: attendanceDraft.maxAttendees,
        windowStartOffsetMin: attendanceDraft.windowStartOffsetMin,
        windowEndHours: attendanceDraft.windowEndHours,
      }).unwrap();
      return true;
    } catch (error) {
      toast.error(
        getActionErrorMessage(
          "El evento se guardó, pero no se pudo guardar la configuración de asistencia",
          "el servidor rechazó la configuración de asistencia",
          error
        )
      );
      return false;
    }
  };

  const attendanceConfigSave = useAttendanceConfigSave((eventId) => persistAttendanceConfig(eventId, true));
  const [isCompletingCreation, setIsCompletingCreation] = useState(false);
  const attendanceValidationErrors = getAttendanceConfigErrors(attendanceDraft);
  const attendanceValidationMessage = Object.values(attendanceValidationErrors)[0];

  const getStartDateMinDate = () => {
    const minD = new Date(now);
    minD.setSeconds(0, 0);
    const regDeadline = values.registrationDeadlineMinutes || 0;
    minD.setMinutes(minD.getMinutes() + regDeadline);
    return minD;
  };

  const afterMutation = () => {
    resetForm({ values });
    clearUnsavedChangesGuard();
    navigate(-1);
  };

  const handleUpdateScope = (updateAll: boolean) => {
    setShowUpdateScopeModal(false);

    if (!pendingFormData) return;

    if (updateAll) {
      // When editing all recurring events, preserve the original date and only apply time changes
      const normalizedStartDate = mergeTimeIntoDate(
        eventData?.startDate,
        pendingFormData.startDate
      );
      const recurrentStartDate = normalizedStartDate
        ? normalizedStartDate.toISOString()
        : pendingFormData.startDate;

      // Extract featured attributes so they don't propagate to all recurring events
      const { featured, featuredOrder, ...restFormData } = pendingFormData;

      const payloadForRecurrents = {
        ...restFormData,
        startDate: recurrentStartDate,
      };

      updateRecurrentEvents(payloadForRecurrents)
        .unwrap()
        .then(() => {
          // Apply the full payload (including featured status) ONLY to the event currently being edited
          updateEvent(pendingFormData)
            .unwrap()
            .then((response: unknown) => {
              notifyBonusCheckins(response, pendingFormData);
              //-1 because recurrentEvents arr contains base event 
              //(event currently being modified)
              const recurrentAmount = recurrentEvents ? recurrentEvents.length : 0;
              toast.success(`Este evento y sus recurrencias (${(recurrentAmount - 1)}) han sido modificadas`);
              persistAttendanceConfig(eventData?.id ?? pendingFormData.id).then((configSaved) => {
                if (configSaved) afterMutation();
              });
            })
            .catch((error) => {
              toast.error(
                getActionErrorMessage(
                  "La recurrencia fue modificada, pero ocurrió un error al actualizar el evento principal",
                  "el servidor rechazó la actualización",
                  error
                )
              );
            });
        })
        .catch((error) => {
          toast.error(
            getActionErrorMessage(
              "No se pudieron modificar el evento y sus recurrencias",
              "el servidor rechazó la actualización",
              error
            )
          );
        });

      return;
    } else {
      // Update only the current event
      updateEvent(pendingFormData)
        .unwrap()
        .then(async (response: unknown) => {
          toast.success("Evento modificado exitosamente!");
          notifyBonusCheckins(response, pendingFormData);
          const configSaved = await persistAttendanceConfig(eventData?.id ?? pendingFormData.id);
          if (configSaved) afterMutation();
        })
        .catch((error) => {
          toast.error(
            getActionErrorMessage(
              "No se pudo modificar el evento",
              "el servidor rechazó la actualización",
              error
            )
          );
        });
    }

    setPendingFormData(null);
  };


  //date constant to compare dates for recurrence calendar (validate end date is at least 1 day older)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize time to midnight

  const isSubmitLoading =
    isLoadingCreate ||
    isLoadingUpdate ||
    isLoadingRecurrent ||
    isLoadingRecurrentUpdate ||
    isLoadingRecurrentCreate || isSavingAttendanceConfig || attendanceConfigSave.isSaving || isCompletingCreation;
  const isSubmitBlocked = isEntityFetching || isSubmitLoading || !isAttendanceConfigReady;
  const hasPendingAttendanceConfig = attendanceConfigSave.pendingEventIds.length > 0;
  const eventFieldsetProps = {
    disabled: hasPendingAttendanceConfig,
    ...(hasPendingAttendanceConfig ? { inert: "" } : {}),
    style: { border: 0, padding: 0, margin: 0, minWidth: 0 },
  };

  const {
    handleSubmit,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    submitCount,
    isSubmitting,
    isValidating,
    resetForm,
    dirty,
  } = useFormik({
    initialValues: {
      id: entity?.id || 0,
      isRecurring: entity?.isRecurring || false,
      endDate: entity?.endDate ? new Date(entity.endDate) : null,
      name: entity?.name || "",
      eventTypeId: entity?.eventTypeId || "",
      eventSubTypeId: entity?.eventSubTypeId || "",
      description: entity?.description || "",
      guests: getGuests(entity?.guests) || "",
      amount: entity?.amount?.toString() || "",
      price: entity?.price !== null && entity?.price !== undefined ? String(entity.price) : "",
      inscription: entity?.inscription !== null && entity?.inscription !== undefined ? String(entity.inscription) : "",
      duration: entity?.duration || (() => { const d = new Date(); d.setHours(1, 0, 0, 0); return d.toISOString(); })(),
      active: resolveEventFormActive(entity),
      startDate: (() => {
        if (entity?.startDate) return new Date(entity.startDate);
        const defaultStart = new Date(now);
        defaultStart.setSeconds(1, 0); // 1 sec extra
        const regDeadline = entity?.registrationDeadline ?? 60;
        defaultStart.setMinutes(defaultStart.getMinutes() + regDeadline + 5); // 5 minutes extra
        return defaultStart;
      })(),
      address: entity?.address || "",
      space: entity?.space || "",
      rules: entity?.rules || "",
      imageFormat: entity?.imageFormat || "",
      imageBase64: entity?.imageBase64 || "",
      imageUrls: entity?.imageUrls || [],
      tenantId: resolveFormTenantId(entity?.tenantId),
      importantMessage: entity?.importantMessage || "",
      hostAssists: !!entity?.hostAssists || false,
      venueId: entity?.venueId || null,
      registrationDeadlineMinutes: entity?.registrationDeadline ?? 60,
      featured: !!entity?.featured,
      featuredOrder: entity?.featuredOrder || null,
      inscriptionStartDateEnabled: !!entity?.inscriptionStartDate,
      inscriptionStartDate: entity?.inscriptionStartDate ? new Date(entity.inscriptionStartDate) : null,
      suppressNotifications: !!entity?.suppressNotifications,
      suppressRatingPrompt: !!entity?.suppressRatingPrompt,
      recurrenceSignupWeeks: entity?.recurrenceSignupWeeks ?? 0,
      ...resolveEventFormCtaValues(entity),
      ...resolveEventFormBonusValues(entity),
    } as NewEventForm,
    enableReinitialize: !!entity,
    validationSchema: Yup.object({
      name: Yup.string().required(yupConstants().required),
      eventTypeId: Yup.string().required(yupConstants().required),
      eventSubTypeId: Yup.string().required(yupConstants().required),
      description: Yup.string().required(yupConstants().required),
      guests: Yup.number().required(yupConstants().required),
      amount: Yup.number()
        .required(yupConstants().required)
        /* El chequeo del checkbox mira los cupos que había al tildarlo. Si después
           se bajan "Cupos totales", el toggle queda activo sin lugar y el guardado
           produciría justamente el sobre-cupo que este cambio evita, así que la
           condición se revalida acá y bloquea el submit sobre el campo culpable. */
        .test(
          "host-assists-fits",
          "Los cupos no alcanzan para el host",
          function (value) {
            // Solo aplica al lugar que consume activar el toggle: si el host ya
            // participaba, su inscripción ya está contada en occupiedSpots.
            if (!this.parent.hostAssists || entity?.hostAssists) {
              return true;
            }

            if (
              hasSpotForParticipatingHost({
                totalSpots: Number(value ?? 0),
                occupiedSpots,
                hostAlreadyRegistered,
              })
            ) {
              return true;
            }

            return this.createError({
              message: `Hay ${occupiedSpots} ${occupiedSpots === 1 ? "lugar ocupado" : "lugares ocupados"} y el host ocupa uno más. Subí los cupos a ${(occupiedSpots ?? 0) + 1} o desactivá "Host participa en el evento".`,
            });
          }
        ),
      inscription: Yup.number().required(yupConstants().required),
      duration: Yup.date()
        .required(yupConstants().required)
        .typeError(yupConstants().required),
      startDate: Yup.date()
        .required(yupConstants().required)
        .test(
          "start-date-after-registration-deadline",
          "La hora de inicio seleccionada es muy cercana. Debes dejar margen para el cierre de inscripciones.",
          function (value) {
            const { registrationDeadlineMinutes } = this.parent;
            if (!value) return true;
            const regDeadline = registrationDeadlineMinutes || 0;
            if (
              entity?.id &&
              value.getTime() === new Date(entity.startDate).getTime() &&
              regDeadline === (entity.registrationDeadline ?? 60)
            ) return true;
            const registrationClosureDate = new Date(value);
            registrationClosureDate.setMinutes(registrationClosureDate.getMinutes() - regDeadline);

            if (registrationClosureDate.getTime() < Date.now()) {
              const formatTimeText = (mins: number) => {
                if (mins < 60) return `${mins} minutos`;
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                if (m === 0) return `${h} ${h === 1 ? "hora" : "horas"}`;
                return `${h}:${m.toString().padStart(2, "0")} hs`;
              };
              const timeText = formatTimeText(regDeadline);
              return this.createError({
                message: `Para el cierre de inscripciones actual (${timeText} antes), el evento debe iniciar más tarde.`
              });
            }
            return true;
          }
        )
        .test(
          "featured-event-in-future",
          "La fecha del evento debe ser futura para destacarlo",
          function (value) {
            if (!this.parent.featured || !value) return true;
            return value.getTime() > Date.now();
          }
        ),
      venueId: Yup.number()
        .nullable()
        .required(yupConstants().required),
      address: Yup.string().required(yupConstants().required),
      space: Yup.string().required(yupConstants().required),
      tenantId: Yup.string().required(yupConstants().required),
      endDate: Yup.date()
        .nullable()
        .when("isRecurring", {
          is: true,
          then: (schema) => schema
            .test(
              "is-required",
              yupConstants().required,
              function (value) {
                if (this.parent.id) return true; // skip validation if editing existing event
                return !!value;
              }
            )
            .test(
              "valid-recurrence-range",
              "La fecha final debe generar al menos 2 eventos en la recurrencia",
              function (value) {
                if (this.parent.id) return true; // skip validation if editing existing event
                const { startDate, recurrence } = this.parent;

                if (!value || !startDate || !recurrence) return true;

                return generateRecurringDates(startDate, value, recurrence).length >= 2;
              }
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      featuredOrder: Yup.number()
        .nullable()
        .when("featured", {
          is: true,
          then: (schema) => schema
            .required("Orden requerido")
            .min(1, "Orden debe ser entre 1 y 5")
            .max(5, "Orden debe ser entre 1 y 5"),
          otherwise: (schema) => schema.notRequired(),
        }),
      inscriptionStartDate: Yup.date()
        .nullable()
        .when("inscriptionStartDateEnabled", {
          is: true,
          then: (schema) => schema
            .required(yupConstants().required)
            .test(
              "inscription-start-not-in-past",
              "La fecha de inicio de inscripciones no puede ser anterior a la fecha actual",
              function (value) {
                if (!value) return true;
                /* Mismo criterio que "start-date-after-registration-deadline": una regla
                   temporal no puede bloquear el guardado de un evento cuya apertura no se
                   tocó. La de una serie en curso ya pasó, así que sin esto no se podría
                   editar ningún otro campo de sus eventos. */
                if (
                  entity?.id &&
                  entity.inscriptionStartDate &&
                  value.getTime() === new Date(entity.inscriptionStartDate).getTime()
                ) return true;
                return value.getTime() >= Date.now();
              }
            )
            .test(
              "inscription-start-before-event-start",
              "La fecha de inicio de inscripciones debe ser anterior a la fecha del evento",
              function (value) {
                const { startDate } = this.parent;
                if (!value || !startDate) return true;
                return value.getTime() < new Date(startDate).getTime();
              }
            )
            .test(
              "inscription-start-before-registration-deadline",
              "La fecha de inicio de inscripciones debe ser anterior al cierre de inscripciones",
              function (value) {
                const { startDate, registrationDeadlineMinutes } = this.parent;
                if (!value || !startDate) return true;
                const regDeadline = registrationDeadlineMinutes || 0;
                const registrationClosureDate = new Date(startDate);
                registrationClosureDate.setMinutes(registrationClosureDate.getMinutes() - regDeadline);
                return value.getTime() < registrationClosureDate.getTime();
              }
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
      recurrenceSignupWeeks: Yup.number()
        .nullable()
        .typeError("Debe ser un número entero")
        .integer("Debe ser un número entero")
        .min(0, "Debe ser entre 0 y 12")
        .max(12, "Debe ser como máximo 12 semanas"),
      buttonType: Yup.string()
        .oneOf(["INSCRIPTION", "CUSTOM_LINK", "NONE"])
        .required(yupConstants().required),
      buttonLabel: Yup.string()
        .nullable()
        .max(40, "Máximo 40 caracteres")
        .when("buttonType", {
          is: "CUSTOM_LINK",
          then: (schema) =>
            schema.required("La etiqueta del botón es obligatoria").trim(),
        }),
      buttonUrl: Yup.string()
        .nullable()
        .when("buttonType", {
          is: "CUSTOM_LINK",
          then: (schema) =>
            schema
              .required("La URL es obligatoria")
              .matches(/^https:\/\//, "La URL debe iniciar con https://"),
        }),
      /* Mismas reglas que el backend (PTS-018): entero mayor a 0 y, si el tenant tiene
         tope, sin pasarlo. Solo con la sección visible: oculta, el campo no viaja. */
      bonusPoints: Yup.number()
        .nullable()
        .typeError("Debe ser un número entero")
        .test("event-bonus-points", "Ingresá los puntos extra", function (value) {
          if (!showBonusSection || !this.parent.bonusEnabled) return true;
          if (value == null) return this.createError({ message: "Ingresá los puntos extra" });
          if (!Number.isInteger(value) || value < 1) {
            return this.createError({ message: "Debe ser un número entero mayor a 0" });
          }
          if (value > MAX_EVENT_BONUS_POINTS) {
            return this.createError({ message: `Como máximo ${MAX_EVENT_BONUS_POINTS} puntos` });
          }
          // Como el backend: el tope solo se valida si el bonus cambia. Si el tope bajó después
          // de guardarlo, el evento se tiene que poder seguir editando.
          const keepsSavedBonus = entity?.bonusPoints != null && value === entity.bonusPoints;
          if (eventBonusMaxPoints !== null && value > eventBonusMaxPoints && !keepsSavedBonus) {
            return this.createError({
              message: `El tope del tenant es de ${formatPointRewardCost(eventBonusMaxPoints)}`,
            });
          }
          return true;
        }),
    }),
    onSubmit: (values) => {
      if (isSubmitBlocked) return;
      if (attendanceValidationMessage) {
        toast.error(attendanceValidationMessage);
        return;
      }

      const siblingIds = new Set(recurrentEvents?.map((re) => re.id) ?? []);
      const takenOrders = (occupiedFeaturedOrdersRef.current ?? [])
        .filter((entry) => entry.id !== values.id && !siblingIds.has(entry.id))
        .map((entry) => entry.order);
      if (values.featured && values.featuredOrder && takenOrders.includes(values.featuredOrder)) {
        toast.error(`La posición ${values.featuredOrder} de evento destacado ya está ocupada para ${getFeaturedTenantLabel(values.tenantId)}.`);
        return;
      }

      const {
        eventTypeId,
        eventSubTypeId,
        guests,
        amount,
        price,
        inscription,
        isRecurring,
        recurrence,
        endDate,
        tenantId,
        registrationDeadlineMinutes,
        inscriptionStartDateEnabled,
        inscriptionStartDate,
        bonusEnabled,
        bonusPoints,
        ...rest
      } = values;

      const isFeatured = !!values.featured;
      const formEventTypeId = eventTypeId ? Number(eventTypeId) : null;
      const formEventSubTypeId = eventSubTypeId ? Number(eventSubTypeId) : null;
      const suppressionPanelVisible =
        !isAdvancedConfigRulesLoaded ||
        isEventNotificationSuppressionVisible(advancedConfigVisibilityRules, {
          tenantId: tenantId || null,
          eventTypeId: Number.isFinite(formEventTypeId) ? formEventTypeId : null,
          eventSubTypeId: Number.isFinite(formEventSubTypeId) ? formEventSubTypeId : null,
          isRecurrent:
            !!isRecurring ||
            !!entity?.recurrenceGroupId ||
            (recurrentEvents?.length ?? 0) > 1,
        });

      const payload = {
        ...rest,
        active: values.active,
        eventTypeId: parseInt(eventTypeId),
        eventSubTypeId: parseInt(eventSubTypeId),
        guests: parseInt(guests),
        amount: parseInt(amount),
        price: price ? parseInt(price) : null,
        inscription: parseInt(inscription),
        tenantId: tenantId || null,
        importantMessage: values.importantMessage || null,
        venueId: values.venueId || null,
        paymentType: (parseInt(inscription) === 0 ? "FREE" : "GRUPS") as "FREE" | "GRUPS",
        startDate: values.startDate instanceof Date ? values.startDate.toISOString() : values.startDate,
        endDate: values.endDate instanceof Date ? values.endDate.toISOString() : values.endDate,
        isRecurring: !!isRecurring,
        inscriptionStartDate: inscriptionStartDateEnabled && inscriptionStartDate
          ? (inscriptionStartDate instanceof Date ? inscriptionStartDate.toISOString() : inscriptionStartDate)
          : null,
        ...buildRegistrationDeadlinePayload(registrationDeadlineMinutes),
        featured: isFeatured,
        featuredOrder: isFeatured ? values.featuredOrder ?? null : null,
        suppressNotifications: suppressionPanelVisible && !!values.suppressNotifications,
        suppressRatingPrompt: suppressionPanelVisible && !!values.suppressRatingPrompt,
        // 0 = null = pre-inscripción desactivada
        recurrenceSignupWeeks:
          suppressionPanelVisible &&
            values.recurrenceSignupWeeks &&
            values.recurrenceSignupWeeks > 0
            ? values.recurrenceSignupWeeks
            : null,
        ...buildEventCtaPayload(values),
        ...buildEventBonusPayload({ bonusEnabled, bonusPoints }, showBonusSection),
      };

      if (!!eventData) {
        // Check if the event is recurring and has recurrent events
        if (recurrentEvents && recurrentEvents.length > 1) {
          // Store the form data and show modal
          setPendingFormData(payload as any);
          setShowUpdateScopeModal(true);

          return;
        } else {
          // Not recurring or only one event, update normally
          updateEvent(payload as any)
            .unwrap()
            .then(async (response: unknown) => {
              toast.success("Evento modificado exitosamente!");
              notifyBonusCheckins(response, payload as EventFetch);
              const configSaved = await persistAttendanceConfig(values.id);
              if (configSaved) afterMutation();
            })
            .catch((error) => {
              toast.error(
                getActionErrorMessage(
                  "No se pudo modificar el evento",
                  "el servidor rechazó la actualización",
                  error
                )
              );
            });
          return;
        }
      }

      if (isRecurring && recurrence && endDate) {
        const dates = generateRecurringDates(values.startDate, endDate, recurrence);

        if (dates.length < 2) {
          toast.error("La recurrencia debe generar al menos 2 eventos");
          return;
        }

        const newEvents =
          dates.map((date, index) => {
            const recurrentStartDate = date.toISOString();
            const isFirst = index === 0;
            const newPayload = {
              ...payload,
              startDate: recurrentStartDate,
              endDate: null,
              tenantId: tenantId || null,
              featured: isFirst ? payload.featured : false,
              featuredOrder: isFirst ? payload.featuredOrder : null,
            }
            return newPayload
          })

        const dto: createEventAndRecurrentsDTO = {
          events: newEvents as any,
          frequency: recurrence.toUpperCase(),
          finalDate: endDate
        }
        toast.loading(`Creando ${dates.length} eventos recurrentes...`, { duration: 3000 });
        createEventAndRecurrents(dto).unwrap()
          .then(async (response) => {
            setIsCompletingCreation(true);
            toast.success(`Se crearon ${dates.length} eventos recurrentes`);
            if (payload.hostAssists && defaultHost?.id) {
              await Promise.all(response.insertedEventIds.map((eventId) => {
                return updateEventHost({
                  id: parseInt(eventId),
                  hostId: defaultHost.id,
                  description: DefaultHost.hostDescription,
                  imageFormat: DefaultHost.hostImageFormat,
                  imageBase64: DefaultHost.hostImageBase64,
                  hostAssists: payload.hostAssists
                }).unwrap().then().catch((error) => {
                  toast.error(
                    getActionErrorMessage(
                      `El evento ${eventId} fue creado pero no se pudo asignar el host`,
                      "falló la asignación automática del anfitrión",
                      error
                    )
                  );
                });
              }));
            }
            const configSaved = !shouldPersistAttendanceConfig(attendanceDraft) ||
              await attendanceConfigSave.save(response.insertedEventIds.map(Number));
            setIsCompletingCreation(false);
            if (configSaved) afterMutation();
          })
          .catch((error) => {
            toast.error(
              getActionErrorMessage(
                "No se pudieron crear los eventos recurrentes",
                "el servidor rechazó la creación",
                error
              )
            );
          }
          );
        return;
      }

      createEvent(payload as any)
        .unwrap()
        .then((response) => {
          setIsCompletingCreation(true);
          toast.success("Evento creado exitosamente!");
          if (defaultHost?.id) {
            return updateEventHost({
              id: response.id,
              hostId: defaultHost.id,
              description: DefaultHost.hostDescription,
              imageFormat: DefaultHost.hostImageFormat,
              imageBase64: DefaultHost.hostImageBase64,
              hostAssists: payload.hostAssists
            })
              .unwrap()
              .then(() => {
                toast.success("Host asignado exitosamente!");
              })
              .catch((error) => {
                toast.error(
                  getActionErrorMessage(
                    "El evento fue creado pero no se pudo asignar el host",
                    "falló la asignación automática del anfitrión",
                    error
                  )
                );
              })
              .finally(async () => {
                const configSaved = !shouldPersistAttendanceConfig(attendanceDraft) ||
                  await attendanceConfigSave.save([response.id]);
                setIsCompletingCreation(false);
                if (configSaved) afterMutation();
              });
          }
          return (shouldPersistAttendanceConfig(attendanceDraft)
            ? attendanceConfigSave.save([response.id])
            : Promise.resolve(true)).then((configSaved) => {
            setIsCompletingCreation(false);
            if (configSaved) afterMutation();
          });

        })

        .catch((error) => {
          //Handle Service Unavailable (CC API Failed to create group)
          if (error.status === 503) {
            toast("El evento fue creado, pero falló la creación automática del chat", { icon: "⚠️" })
            afterMutation();
          }
          else {
            toast.error(
              getActionErrorMessage(
                "No se pudo crear el evento",
                "el servidor rechazó la creación",
                error
              )
            );
          }

        });


    }
  });

  // Avisa antes de que un cambio de tenant en el header recargue la pantalla
  // y se lleve puesto lo que se estaba cargando.
  const clearUnsavedChangesGuard = useUnsavedChangesGuard(dirty);

  /**
   * GTK-717 / TC-PAY-15. El host que participa se inscribe y su fila ocupa un
   * cupo real, así que sobre un evento lleno el toggle dejaba el evento
   * sobre-cupado. Decisión de Producto: no se desplaza a ningún asistente ya
   * confirmado — se avisa que no hay lugar y el toggle no se activa.
   *
   * El cupo se mide contra el `amount` del formulario, no contra el guardado: si
   * el admin amplía los cupos en la misma edición, el host entra sin necesidad de
   * guardar primero.
   */
  const occupiedSpots = resolveOccupiedSpots(entity);

  /**
   * El cupo que decide todo esto es el del formulario, no el guardado. Se calcula
   * con la misma función que después bloquea, así el `skip` de la consulta no se
   * puede desalinear del bloqueo: si se decidieran por separado, bajar los cupos
   * de un evento que estaba holgado bloquearía sin haber llegado a averiguar si
   * el host ya tenía inscripción propia.
   */
  const fitsWithoutExemption = hasSpotForParticipatingHost({
    totalSpots: Number(values.amount || 0),
    occupiedSpots,
    hostAlreadyRegistered: false,
  });

  /**
   * Si el host ya se inscribió por su cuenta, activar el toggle no consume un
   * cupo nuevo: hay que dejarlo pasar igual. Solo se consulta la lista cuando el
   * cupo no da, así la edición de un evento holgado no paga la request.
   */
  const {
    data: eventUsers,
    isLoading: isLoadingEventUsers,
    isFetching: isFetchingEventUsers,
    isError: hostRegistrationCheckFailed,
  } = useGetEventUsersQuery(entity?.id ?? 0, {
    skip: !entity?.id || fitsWithoutExemption,
  });

  // Mientras la lista viaja no se sabe si corresponde la excepción, y `eventUsers`
  // undefined daría "no inscripto" por omisión. Se deshabilita el checkbox en vez
  // de resolver la carrera a favor de un bloqueo incorrecto.
  const isCheckingHostRegistration = isLoadingEventUsers || isFetchingEventUsers;

  const hostAlreadyRegistered =
    entity?.hostId != null &&
    Array.isArray(eventUsers) &&
    eventUsers.some((eventUser) => eventUser.userId === entity.hostId);

  // Si la consulta falla se bloquea igual: solo corre cuando el cupo ya no da, así
  // que el bloqueo es correcto salvo por la excepción, que no se puede confirmar.
  // El diálogo lo aclara en vez de afirmar algo que no verificamos.
  const hasSpotForHost = fitsWithoutExemption || hostAlreadyRegistered;

  /**
   * Advertencia visual (no bloqueante) cuando los cupos totales superan el
   * `maxCapacity` del venue elegido. `amount` ya contempla los invitados por
   * persona del lado del negocio, así que acá no se vuelve a multiplicar por
   * `guests` (antes se usaba `calculateRequiredSpots`, que duplicaba ese
   * cálculo).
   * Se consulta el venue acá además de en VenueSelector porque este último no
   * expone el objeto completo al padre; RTK Query comparte la misma entrada
   * de caché, así que no duplica el request.
   */
  const { data: selectedVenueForCapacity } = useGetVenueByIdQuery(
    values.venueId || "",
    { skip: !values.venueId }
  );

  const totalAttendees = Number(values.amount || 0);

  const venueOverCapacity = exceedsVenueCapacity(
    selectedVenueForCapacity?.maxCapacity,
    totalAttendees
  );

  const occupiedFeaturedTenantId = values.tenantId || "";

  /**
   * GTK-887 — La sección de puntos extra solo se muestra con la acumulación de puntos
   * habilitada y la regla event_bonus del tenant activa. Mientras flag o reglas cargan
   * (o si fallan) queda oculta, y oculta no manda bonusPoints: el bonus guardado no se toca.
   */
  const pointsFlag = useIsFeatureFlagEnabled(
    "gamification_points_enabled",
    values.tenantId || null
  );
  const { currentData: tenantPointRules } = useGetPointRulesQuery(values.tenantId || "", {
    skip: !values.tenantId || pointsFlag.loading || !pointsFlag.enabled,
  });
  const eventBonusRule = findActiveEventBonusRule(tenantPointRules);
  const showBonusSection = pointsFlag.enabled && eventBonusRule !== null;
  const eventBonusMaxPoints = resolveEventBonusMaxPoints(eventBonusRule);
  // Cómo llama el tenant a sus puntos, para la banda del preview ("Asistí y sumá Puntos").
  const { currentData: pointSettings } = useGetPointSettingsQuery(undefined, {
    skip: !showBonusSection,
  });
  const appCurrencyName = pointSettings?.effectiveCurrencyName ?? DEFAULT_POINT_CURRENCY_NAME;

  /**
   * PR #175 — el catálogo de tipos/subtipos es por tenant. `values.tenantId` lo
   * llena resolveFormTenantId desde el scope del header, así que sigue siendo la
   * fuente correcta aunque el formulario ya no tenga su propio campo de Tenant
   * (INC-05 lo eliminó).
   */
  const { data: dataEventsTypes, isLoading: isLoadingTypes } = useGetEventTypesQuery(
    values.tenantId ? { tenantId: values.tenantId } : undefined
  );

  const storedEventTypeId = entity?.eventTypeId ?? null;
  const storedEventTypeName = entity?.eventTypeName ?? "";
  const storedEventSubTypeId = entity?.eventSubTypeId ?? null;
  const storedEventSubTypeName = entity?.eventSubTypeName ?? "";
  const selectedEventTypeId = values.eventTypeId ? Number(values.eventTypeId) : null;

  /* Al editar, si el tipo guardado ya no está en el catálogo del tenant se lo
     agrega igual: si no, el Dropdown quedaría vacío y guardar borraría el dato. */
  const eventTypeOptions = React.useMemo(() => {
    const catalogTypes = Array.isArray(dataEventsTypes) ? dataEventsTypes : [];
    const storedTypeMissingFromCatalog =
      storedEventTypeId !== null &&
      !!storedEventTypeName &&
      !catalogTypes.some((type) => type.eventTypeId === storedEventTypeId);
    if (!storedTypeMissingFromCatalog) return catalogTypes;
    return [
      ...catalogTypes,
      { eventTypeId: storedEventTypeId, eventTypeName: storedEventTypeName, subTypes: [] },
    ];
  }, [dataEventsTypes, storedEventTypeId, storedEventTypeName]);

  const eventSubTypeOptions = React.useMemo(() => {
    if (selectedEventTypeId === null) return [];
    const catalogSubTypes =
      eventTypeOptions.find((type) => type.eventTypeId === selectedEventTypeId)?.subTypes ?? [];
    const storedSubTypeMissingFromCatalog =
      selectedEventTypeId === storedEventTypeId &&
      storedEventSubTypeId !== null &&
      !!storedEventSubTypeName &&
      !catalogSubTypes.some((subType) => subType.eventSubTypeId === storedEventSubTypeId);
    if (!storedSubTypeMissingFromCatalog) return catalogSubTypes;
    return [
      ...catalogSubTypes,
      {
        eventSubTypeId: storedEventSubTypeId,
        eventSubTypeName: storedEventSubTypeName,
        eventTypeId: selectedEventTypeId,
        createDate: "",
      },
    ];
  }, [
    eventTypeOptions,
    selectedEventTypeId,
    storedEventTypeId,
    storedEventSubTypeId,
    storedEventSubTypeName,
  ]);

  const { data: occupiedFeaturedOrders } = useGetOccupiedFeaturedOrdersQuery(
    occupiedFeaturedTenantId,
    { skip: !values.featured || !occupiedFeaturedTenantId, refetchOnMountOrArgChange: true }
  );

  occupiedFeaturedOrdersRef.current = occupiedFeaturedOrders;

  const getTakenFeaturedOrders = React.useCallback(
    (eventId?: number) => {
      const siblingIds = new Set(recurrentEvents?.map((re) => re.id) ?? []);
      return (occupiedFeaturedOrders ?? [])
        .filter((entry) => entry.id !== eventId && !siblingIds.has(entry.id))
        .map((entry) => entry.order);
    },
    [occupiedFeaturedOrders, recurrentEvents]
  );

  const isFeaturedOrderTaken = React.useCallback(
    (order: number, eventId?: number) => getTakenFeaturedOrders(eventId).includes(order),
    [getTakenFeaturedOrders]
  );

  const getAvailableFeaturedOrders = () => {
    const allOptions = [1, 2, 3, 4, 5];
    const takenOrders = getTakenFeaturedOrders(values.id);
    return allOptions
      .filter((order) => !takenOrders.includes(order))
      .map((n) => ({ label: String(n), value: n }));
  };

  useEffect(() => {
    if (values && !values.isRecurring) setFieldValue("endDate", null);
  }, [values?.isRecurring, values, setFieldValue]);

  const previousTenantIdRef = React.useRef(values.tenantId);

  useEffect(() => {
    const previousTenantId = previousTenantIdRef.current;
    const tenantChanged = previousTenantId !== values.tenantId;
    previousTenantIdRef.current = values.tenantId;

    if (!tenantChanged) return;

    /* PR #175 — el catálogo de tipos es por tenant, así que un tipo elegido para
       el tenant anterior puede no existir en el nuevo. Se limpia para no mandar
       una combinación que el backend rechaza. */
    const catalogSwitchedByUser = !!previousTenantId && !!(values.eventTypeId || values.eventSubTypeId);
    if (catalogSwitchedByUser) {
      setFieldValue("eventTypeId", "");
      setFieldValue("eventSubTypeId", "");
    }

    if (
      values.featured &&
      values.featuredOrder &&
      isFeaturedOrderTaken(values.featuredOrder, values.id)
    ) {
      setFieldValue("featuredOrder", null);
      toast.error("La posición seleccionada no está disponible para este tenant.");
    }
  }, [
    values.tenantId,
    values.eventTypeId,
    values.eventSubTypeId,
    values.featured,
    values.featuredOrder,
    values.id,
    isFeaturedOrderTaken,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!eventData && values && values.featured) setFieldValue("isRecurring", false);
  }, [values?.featured, values, setFieldValue, eventData]);

  // MT-011 — Si el tenant está fijado por el scope, forzar el valor del form.
  // El tenant es obligatorio y el dropdown no tiene opción vacía, así que si /bo/me
  // resolvió después del primer render hay que completar el default a mano.
  useEffect(() => {
    if (tenantScope.locked && tenantScope.tenantId && values.tenantId !== tenantScope.tenantId) {
      setFieldValue("tenantId", tenantScope.tenantId);
      return;
    }
    if (!values.tenantId && boMe?.defaultTenantId) {
      setFieldValue("tenantId", boMe.defaultTenantId);
    }
  }, [
    tenantScope.locked,
    tenantScope.tenantId,
    values.tenantId,
    boMe?.defaultTenantId,
    setFieldValue,
  ]);

  // If the event is recurrent, reliably turn on the 'isRecurring' switch
  useEffect(() => {
    if (recurrentEvents && recurrentEvents.length > 1 && !values.isRecurring) {
      setFieldValue("isRecurring", true);
    }
  }, [recurrentEvents, values?.isRecurring, setFieldValue]);

  useEffect(() => {
    if (submitCount === 0) {
      handledSubmitCount.current = 0;
      setValidationAttempt(0);
      return;
    }
    if (isSubmitting || isValidating || handledSubmitCount.current === submitCount) return;
    handledSubmitCount.current = submitCount;

    const firstError = getFirstErrorMessage(errors);
    if (firstError) {
      setValidationAttempt(submitCount);
      toast.error(firstError);
    }
  }, [submitCount, errors, isSubmitting, isValidating]);

  const submitValidationMessage = validationAttempt > 0 ? getFirstErrorMessage(errors) : null;
  const featuredTenantLabel = getFeaturedTenantLabel(values.tenantId);

  const handleInputFile = (value: string) => {
    setFieldError("imageBase64", undefined);
    // Store S3 URL in a backwards-compatible way for the mobile app.
    // Mobile does: imageFormat + "," + imageBase64 → "https://...#,"
    // The fragment "#," is ignored in HTTP, so the image loads from the S3 URL.
    setFieldValue("imageFormat", value + "#");
    setFieldValue("imageBase64", "");
  };
  const handleInputFileClear = () => {
    setFieldValue("imageFormat", "");
    setFieldValue("imageBase64", "");
  };

  const durationValue = values?.duration ? parseDate(values.duration) : new Date();
  const registrationDeadlineMinutes = values.registrationDeadlineMinutes ?? 60;
  const isEditingRecurrentGroup = !!eventData && ((recurrentEvents?.length ?? 0) > 1 || values.isRecurring);
  const formTenantId = values.tenantId || null;
  const formEventTypeId = values.eventTypeId ? Number(values.eventTypeId) : null;
  const formEventSubTypeId = values.eventSubTypeId ? Number(values.eventSubTypeId) : null;
  const isRecurrentForAdvancedConfig =
    !!values.isRecurring ||
    !!entity?.recurrenceGroupId ||
    (recurrentEvents?.length ?? 0) > 1;
  const showAdvancedConfig = isEventNotificationSuppressionVisible(
    advancedConfigVisibilityRules,
    {
      tenantId: formTenantId,
      eventTypeId: Number.isFinite(formEventTypeId) ? formEventTypeId : null,
      eventSubTypeId: Number.isFinite(formEventSubTypeId) ? formEventSubTypeId : null,
      isRecurrent: isRecurrentForAdvancedConfig,
    }
  );
  const isSuppressionActive =
    !!values.suppressNotifications || !!values.suppressRatingPrompt;

  useEffect(() => {
    if (!isAdvancedConfigRulesLoaded || isEntityFetching || showAdvancedConfig) return;
    // Sin tenantId resuelto la regla no se puede evaluar y showAdvancedConfig da
    // false por defecto: resetear acá borraria la config de un evento que si aplica.
    if (!formTenantId) return;
    if (
      !values.suppressNotifications &&
      !values.suppressRatingPrompt &&
      !values.recurrenceSignupWeeks
    ) {
      return;
    }

    setFieldValue("suppressNotifications", false);
    setFieldValue("suppressRatingPrompt", false);
    setFieldValue("recurrenceSignupWeeks", 0);
  }, [
    isAdvancedConfigRulesLoaded,
    isEntityFetching,
    showAdvancedConfig,
    formTenantId,
    values.suppressNotifications,
    values.suppressRatingPrompt,
    values.recurrenceSignupWeeks,
    setFieldValue,
  ]);

  return (
    <form className="neutral-input-controls" onSubmit={(event) => {
      if (attendanceConfigSave.pendingEventIds.length === 0) {
        handleSubmit(event);
        return;
      }
      event.preventDefault();
      if (attendanceValidationMessage) {
        toast.error(attendanceValidationMessage);
        return;
      }
      if (isSubmitBlocked) return;
      void attendanceConfigSave.save(attendanceConfigSave.pendingEventIds).then((saved) => {
        if (saved) afterMutation();
      });
    }}>
      {attendanceConfigSave.pendingEventIds.length > 0 && (
        <Message severity="warn" text="El evento ya se creó. Falta guardar la configuración de asistencia; reintentá sin volver a crear el evento." className="w-full mb-3" />
      )}
      <FormSections validationAttempt={validationAttempt}>
        <FormSection title="Información general">
          <fieldset {...eventFieldsetProps}>
          <div className="grid">
            <div className="col-12 my-2">
              <span className="p-float-label">
                <InputText
                  invalid={!!touched.name && !!errors.name}
                  className="w-full"
                  id="name"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="name">Nombre</label>
              </span>
              {touched.name && errors.name && (
                <small className="p-error">{errors.name}</small>
              )}
            </div>
            <div className="col my-2">
              <span className="p-float-label">
                <Dropdown
                  invalid={!!touched.eventTypeId && !!errors.eventTypeId}
                  className="w-full"
                  id="eventTypeId"
                  name="eventTypeId"
                  onChange={(e) => {
                    setFieldValue("eventTypeId", e.value);
                    setFieldValue("eventSubTypeId", ""); // Clear subtype when type changes
                  }}
                  onBlur={handleBlur}
                  value={values.eventTypeId}
                  optionLabel="eventTypeName"
                  optionValue="eventTypeId"
                  options={eventTypeOptions}
                  loading={isLoadingTypes}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="eventTypeId">Tipo</label>
              </span>
              {touched.eventTypeId && errors.eventTypeId && (
                <small className="p-error">{errors.eventTypeId}</small>
              )}
            </div>
            <div className="col my-2">
              <span className="p-float-label">
                <Dropdown
                  invalid={!!touched.eventSubTypeId && !!errors.eventSubTypeId}
                  className="w-full"
                  id="eventSubTypeId"
                  name="eventSubTypeId"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.eventSubTypeId}
                  optionLabel="eventSubTypeName"
                  optionValue="eventSubTypeId"
                  options={eventSubTypeOptions}
                  emptyMessage="Debe seleccionar un tipo"
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="eventSubTypeId">Sub-Tipo</label>
              </span>
              {touched.eventSubTypeId && errors.eventSubTypeId && (
                <small className="p-error">{errors.eventSubTypeId}</small>
              )}
            </div>

          </div>
          <div className="col-12 my-2">
            <span className="p-label">
              <label htmlFor="description">Descripción</label>
              <InputTextarea
                className={`min-w-full ${touched.description && errors.description ? "p-invalid" : ""
                  }`}
                id="description"
                name="description"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description}
                rows={3}
                autoResize
                disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
              />
            </span>
            {touched.description && errors.description && (
              <small className="p-error">{errors.description}</small>
            )}
          </div>
          <div className="col-12 my-2">
            <span className="p-label">
              <label htmlFor="importantMessage">Mensaje Importante (Opcional)</label>
              <InputTextarea
                className="min-w-full"
                id="importantMessage"
                name="importantMessage"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.importantMessage || ""}
                rows={2}
                autoResize
                disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                placeholder="Mensaje destacado que aparecerá en el evento"
              />
            </span>
          </div>
          <div className="col-12 my-2">
            <span className="p-label">
              <label htmlFor="rules">Reglas del evento</label>
              <InputTextarea
                className="w-full"
                id="rules"
                name="rules"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.rules}
                rows={3}
                autoResize
                disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
              />
            </span>
          </div>
          </fieldset>
        </FormSection>
        <FormSection title="Imágenes">
          <fieldset {...eventFieldsetProps}>
          {/* Dos columnas, como el mockup: las adicionales ocupan el ancho útil y
            la portada queda a un costado. Antes la portada tomaba 5/12 sola en
            una fila y las adicionales otra fila entera debajo, con 7/12 vacíos
            al lado de la portada. */}
          <div className="grid align-items-start">
            <div className="col-12 lg:col-4 flex flex-column gap-2">
              <label className="font-semibold">Imagen de portada</label>
              <div className="flex flex-column align-items-center border-1 surface-border border-round p-3">
                <InputFileCustom
                  handleInputFile={handleInputFile}
                  handleInputFileClear={handleInputFileClear}
                  error={errors.imageBase64 && errors.imageBase64}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  cropWidth={1290}
                  cropHeight={400}
                  uploadFolder="covers"
                />
              </div>
            </div>
            <div className="col-12 lg:col-8">
              <ImageUrlsManager
                imageUrls={values.imageUrls || []}
                onChange={(urls) => setFieldValue('imageUrls', urls)}
                label="Imágenes adicionales"
                cropWidth={1290}
                cropHeight={900}
                disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
              />
            </div>
          </div>
          </fieldset>
        </FormSection>
        <FormSection title="Fecha y cupos">
          <fieldset {...eventFieldsetProps}>
          <div className="grid">
            <div className="col-12 md:col-4 my-3">
              <span className="p-float-label">
                <Calendar
                  required
                  invalid={!!touched.startDate && !!errors.startDate}
                  className="w-full"
                  id="startDate"
                  name="startDate"
                  dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
                  locale={CALENDAR_LOCALE}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.startDate}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  showTime
                  hourFormat="24"
                  stepMinute={5}
                  minDate={isEditingRecurrentGroup && eventData?.startDate ? (() => {
                    const d = new Date(eventData.startDate);
                    d.setHours(0, 0, 0, 0);
                    return d;
                  })() : getStartDateMinDate()}
                  maxDate={isEditingRecurrentGroup && eventData?.startDate ? (() => {
                    const d = new Date(eventData.startDate);
                    d.setHours(23, 59, 59, 999);
                    return d;
                  })() : undefined}
                  onFocus={(e) => e.target.blur()}
                  hideOnDateTimeSelect={false}
                  touchUI={false}
                  onShow={() => {
                    const calendarElement = document.querySelector(".p-datepicker");
                    const calendarWrapper = calendarElement?.parentElement;

                    if (calendarWrapper) {
                      calendarWrapper.addEventListener(
                        "scroll",
                        (e) => e.stopPropagation(),
                        { passive: false }
                      );
                    }
                  }}
                  onHide={() => {
                    const calendarElement = document.querySelector(".p-datepicker");
                    const calendarWrapper = calendarElement?.parentElement;

                    if (calendarWrapper) {
                      calendarWrapper.removeEventListener("scroll", (e) => e.stopPropagation());
                    }
                  }}
                />
                <label htmlFor="startDate">Fecha</label>
              </span>
              {isEditingRecurrentGroup && values.startDate && (
                <small className="block mt-1 font-italic text-500">
                  Día {new Date(values.startDate).toLocaleDateString("es-AR")}
                </small>
              )}
              {touched.startDate && errors.startDate && (
                <small className="p-error">{errors.startDate as string}</small>
              )}
            </div>
            <div className="col-12 md:col-4 my-3">
              <span className="p-float-label">
                <DeadlineHourPicker
                  className="w-full p-filled"
                  value={registrationDeadlineMinutes}
                  onChange={(minutes) => setFieldValue("registrationDeadlineMinutes", minutes)}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  minHours={0}
                />
                <label htmlFor="registrationDeadlineMinutes">Cierre de inscripciones</label>
              </span>
              <small className="block mt-1 text-500">
                Tiempo antes del evento en que se cierra la inscripción
              </small>
            </div>
            <div className="col-12 md:col-4 my-3">
              <span className="p-float-label">
                <DeadlineHourPicker
                  className={`w-full p-filled ${!!touched.duration && !!errors.duration ? "p-invalid" : ""}`}
                  value={(durationValue?.getHours() || 0) * 60 + (durationValue?.getMinutes() || 0)}
                  onChange={(minutes) => {
                    const newDuration = new Date();
                    newDuration.setHours(Math.floor(minutes / 60));
                    newDuration.setMinutes(minutes % 60);
                    newDuration.setSeconds(0);
                    newDuration.setMilliseconds(0);
                    setFieldValue("duration", newDuration.toISOString());
                  }}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  minHours={0}
                  panelStyle={{ width: "13rem" }}
                  inputWidthClass="w-3rem"
                />
                <label htmlFor="duration">Duración</label>
              </span>
              {touched.duration && errors.duration && (
                <small className="p-error">{errors.duration}</small>
              )}
            </div>

            <div className="col-12 my-3">
              <div className="flex align-items-center gap-2">
                <div className="flex align-items-center gap-2 mt-2">
                  <label htmlFor="inscriptionStartDateEnabled" className="font-semibold">
                    Fecha de inicio de inscripción
                  </label>
                  <InputSwitch
                    id="inscriptionStartDateEnabled"
                    checked={!!values.inscriptionStartDateEnabled}
                    onChange={(e) => {
                      setFieldValue("inscriptionStartDateEnabled", e.value);
                      if (!e.value) {
                        setFieldValue("inscriptionStartDate", null);
                      }
                    }}
                    disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  />
                </div>
                {values.inscriptionStartDateEnabled && (
                  <span
                    className="p-float-label"
                    /* Acotado en vez de flex-1: estiraba hasta llenar todo el ancho
                       sobrante de la fila y quedaba desproporcionado contra los
                       campos de arriba. */
                    style={{ flex: "0 1 260px", minWidth: 0 }}
                  >
                    <Calendar
                      invalid={!!touched.inscriptionStartDate && !!errors.inscriptionStartDate}
                      className="w-full"
                      id="inscriptionStartDate"
                      name="inscriptionStartDate"
                      dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
                      locale={CALENDAR_LOCALE}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.inscriptionStartDate}
                      disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                      showTime
                      hourFormat="24"
                      stepMinute={5}
                    />
                    <label htmlFor="inscriptionStartDate">Inicio de inscripciones</label>
                  </span>
                )}
              </div>
              {!!values.inscriptionStartDateEnabled && (isEditingRecurrentGroup || !!values.isRecurring) && (
                <small className="block mt-1 text-500">
                  Cada evento de la serie abre su inscripción con esta misma anticipación
                  respecto de su propia fecha, no todos en este día y hora.
                </small>
              )}
              {touched.inscriptionStartDate && errors.inscriptionStartDate && (
                <small className="p-error">{errors.inscriptionStartDate as string}</small>
              )}
            </div>

            <div className="col-12 md:col-4 my-3">
              <span className="p-float-label">
                <InputText
                  invalid={!!touched.amount && !!errors.amount}
                  className="w-full"
                  id="amount"
                  name="amount"
                  keyfilter="num"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.amount}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="amount">Cupos totales</label>
              </span>
              {touched.amount && errors.amount && (
                <small className="p-error">{errors.amount}</small>
              )}

            </div>

            <div className="col-12 md:col-4 my-3">
              <span className="p-float-label">
                <Dropdown
                  className={`w-full ${touched.guests && errors.guests ? "p-invalid" : ""
                    }`}
                  id="guests"
                  name="guests"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.guests}
                  emptyFilterMessage="No se encontraron resultados"
                  emptyMessage="No hay invitados"
                  optionLabel="name"
                  optionValue="value"
                  options={Array.isArray(guestsTypes) ? [...guestsTypes] : []}
                  loading={isLoadingTypes}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="guests">Invitados por persona</label>
              </span>
              {touched.guests && errors.guests && (
                <small className="p-error">{errors.guests}</small>
              )}
            </div>

            {venueOverCapacity && (
              <div className="col-12 pt-0">
                <Message
                  severity="warn"
                  className="w-full justify-content-start"
                  text={`El venue tiene un cupo máximo de ${selectedVenueForCapacity?.maxCapacity} personas y este evento admite hasta ${totalAttendees} (cupos totales). Revisá los cupos o elegí otro venue.`}
                />
              </div>
            )}

            <div className="col-12 md:col-4 my-3 flex flex-column">
              <div className="flex align-items-center">
                <Checkbox
                  id="hostAssists"
                  variant="filled"
                  checked={values.hostAssists}
                  onChange={e => {
                    if (e.checked && !hasSpotForHost) {
                      setShowHostSpotDialog(true);
                      return;
                    }
                    setFieldValue("hostAssists", e.checked)
                  }}
                  disabled={
                    isLoadingCreate ||
                    isLoadingUpdate ||
                    isLoadingRecurrent ||
                    isCheckingHostRegistration
                  }
                ></Checkbox>
                <label htmlFor="hostAssists" className="ml-2">Host participa en el evento</label>
              </div>
              {entity?.hostAssists && (
                <small className="block mt-1">
                  * Desactivar este campo eliminará la inscripción del host actual.
                </small>
              )}
            </div>
          </div>
          </fieldset>
        </FormSection>
        <FormSection title="Acción / CTA">
          <fieldset {...eventFieldsetProps}>
          <div className="grid">
            <div className="col-12 lg:col-4 field">
              <label htmlFor="buttonType">Modalidad del botón *</label>
              <Dropdown
                id="buttonType"
                className="w-full"
                value={values.buttonType}
                options={[
                  { label: "Inscripción estándar", value: "INSCRIPTION" },
                  { label: "Botón personalizado", value: "CUSTOM_LINK" },
                  { label: "Sin botón (informativo)", value: "NONE" },
                ]}
                onChange={(e) => {
                  setFieldValue("buttonType", e.value);
                  if (e.value !== "CUSTOM_LINK") {
                    setFieldValue("buttonLabel", "");
                    setFieldValue("buttonUrl", "");
                    setFieldValue("buttonSendSession", false);
                    setFieldValue("buttonOpenInApp", false);
                  }
                  // Con inscripción externa o sin botón, los cupos y asistentes
                  // no los gestiona la app: el toggle queda bloqueado en false.
                  setFieldValue("showAttendeesInfo", e.value === "INSCRIPTION");
                }}
                placeholder="Seleccionar modalidad"
                disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
              />
              <small className="text-color-secondary block mt-1">
                Define la acción principal del evento en la app.
              </small>
            </div>

            {values.buttonType === "CUSTOM_LINK" && (
              <>
                <div className="col-12 lg:col-4 field">
                  <label htmlFor="buttonLabel">Etiqueta del botón *</label>
                  <InputText
                    id="buttonLabel"
                    className={`w-full ${touched.buttonLabel && errors.buttonLabel ? "p-invalid" : ""}`}
                    value={values.buttonLabel}
                    onChange={(e) => setFieldValue("buttonLabel", e.target.value)}
                    onBlur={() => setFieldTouched("buttonLabel", true)}
                    placeholder="Ej: Ver más info, Reservar lugar"
                    maxLength={40}
                    disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  />
                  {touched.buttonLabel && errors.buttonLabel && (
                    <small className="p-error">{errors.buttonLabel}</small>
                  )}
                </div>

                <div className="col-12 lg:col-4 field">
                  <label htmlFor="buttonUrl">URL destino *</label>
                  <InputText
                    id="buttonUrl"
                    className={`w-full ${touched.buttonUrl && errors.buttonUrl ? "p-invalid" : ""}`}
                    value={values.buttonUrl}
                    onChange={(e) => setFieldValue("buttonUrl", e.target.value)}
                    onBlur={() => setFieldTouched("buttonUrl", true)}
                    placeholder="https://..."
                    disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  />
                  {touched.buttonUrl && errors.buttonUrl && (
                    <small className="p-error">{errors.buttonUrl}</small>
                  )}
                </div>

                <div className="col-12 field">
                  <div className="flex align-items-center gap-3">
                    <InputSwitch
                      id="buttonSendSession"
                      checked={values.buttonSendSession}
                      disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                      onChange={(e) => setFieldValue("buttonSendSession", e.value)}
                    />
                    <label htmlFor="buttonSendSession" className="mb-0">
                      Enviar la sesión del usuario al abrir el link
                    </label>
                  </div>
                  <small className="text-color-secondary block mt-1">
                    Para que el sitio de destino reconozca al usuario sin pedirle que
                    vuelva a ingresar. Solo funciona con nuestros sitios: si el dominio
                    de la URL no está habilitado, el guardado se rechaza.
                  </small>
                </div>

                <div className="col-12 field">
                  <div className="flex align-items-center gap-3">
                    <InputSwitch
                      id="buttonOpenInApp"
                      checked={values.buttonOpenInApp}
                      disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                      onChange={(e) => setFieldValue("buttonOpenInApp", e.value)}
                    />
                    <label htmlFor="buttonOpenInApp" className="mb-0">
                      Abrir dentro de la app
                    </label>
                  </div>
                  <small className="text-color-secondary block mt-1">
                    {values.buttonOpenInApp
                      ? "El link se abre en un navegador embebido, sin salir de la app."
                      : "El link se abre en el navegador del teléfono."}
                  </small>
                </div>
              </>
            )}

            <div className="col-12 field">
              <div className="flex align-items-center gap-3">
                <InputSwitch
                  id="showAttendeesInfo"
                  checked={values.showAttendeesInfo}
                  disabled={
                    values.buttonType !== "INSCRIPTION" ||
                    isLoadingCreate ||
                    isLoadingUpdate ||
                    isLoadingRecurrent
                  }
                  onChange={(e) => setFieldValue("showAttendeesInfo", e.value)}
                />
                <label htmlFor="showAttendeesInfo" className="mb-0">
                  Mostrar datos de asistentes y cupos en la app
                </label>
              </div>
              <small className="text-color-secondary block mt-1">
                {values.buttonType === "INSCRIPTION"
                  ? "Controla si la app muestra los cupos disponibles, el conteo de asistentes y la sección “Asistirán”."
                  : "Solo disponible con “Inscripción estándar”: en las demás modalidades la inscripción no se gestiona en la app y estos datos permanecen ocultos."}
              </small>
            </div>
          </div>
          </fieldset>
        </FormSection>
        <FormSection title="Ubicación">
          <fieldset {...eventFieldsetProps}>
          <div className="grid">
            <VenueSelector
              tenantId={values.tenantId || undefined}
              selectedVenueId={values.venueId || null}
              onVenueSelect={(venue) => {
                if (venue) {
                  setFieldValue("address", venue.address);
                  setFieldValue("venueId", venue.id);
                } else {
                  setFieldValue("address", "");
                  setFieldValue("venueId", null);
                }
              }}
              onBlur={() => setFieldTouched("venueId", true, false)}
              required
              invalid={!!touched.venueId && !!errors.venueId}
              disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
            />
            {touched.venueId && errors.venueId && (
              <div className="col-12 pt-0">
                <small className="p-error">{errors.venueId}</small>
              </div>
            )}
            {venueOverCapacity && (
              <div className="col-12 pt-0">
                <Message
                  severity="warn"
                  className="w-full justify-content-start"
                  text={`El venue tiene un cupo máximo de ${selectedVenueForCapacity?.maxCapacity} personas y este evento admite hasta ${totalAttendees} (cupos totales). Revisá los cupos o elegí otro venue.`}
                />
              </div>
            )}

            <div className="col-12 md:col-6 my-2">
              <span className="p-float-label">
                <InputText
                  invalid={!!touched.venueId && !!values.venueId && !!touched.address && !!errors.address}
                  className="w-full"
                  id="address"
                  name="address"
                  onBlur={handleBlur}
                  value={values.address}
                  placeholder="Se completa automáticamente desde el venue"
                  readOnly
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="address">Dirección</label>
              </span>
              <small className="text-color-secondary block mt-2">
                La dirección se toma del venue seleccionado y ya no puede cargarse manualmente desde este formulario.
              </small>
              {!!touched.venueId && !!values.venueId && touched.address && errors.address && (
                <small className="p-error">{errors.address}</small>
              )}
            </div>
            <div className="col-12 md:col-6 my-2">
              <span className="p-float-label">
                <Dropdown
                  className={`min-w-full ${touched.space && errors.space ? "p-invalid" : ""
                    }`}
                  id="space"
                  name="space"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.space}
                  optionLabel="name"
                  optionValue="value"
                  options={Array.isArray(spacesTypes) ? [...spacesTypes] : []}
                  loading={isLoadingTypes}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="space">Espacio</label>
              </span>
              {touched.space && errors.space && (
                <small className="p-error">{errors.space}</small>
              )}
            </div>
          </div>
          </fieldset>
        </FormSection>
        <FormSection title="Configuración avanzada">
          <fieldset {...eventFieldsetProps}>
          <div className="grid">
            <div className="col-12 mt-2 mb-2">
              <div className="flex align-items-center gap-2 mb-2">
                <label className="font-semibold">Destacado</label>
                <InputSwitch
                  id="featured"
                  checked={!!values.featured}
                  onChange={(e) => {
                    setFieldValue("featured", e.value);
                    if (!e.value) {
                      setFieldValue("featuredOrder", null);
                    } else {
                      setFieldTouched("featuredOrder", false);
                    }
                  }}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="featured" className="font-medium">
                  {values.featured ? "Activado" : "Desactivado"}
                </label>
              </div>

              {values.featured && (
                <div className="grid">
                  <div className="col-12 md:col-3 my-2">
                    <span className="p-float-label">
                      <Dropdown
                        invalid={!!touched.featuredOrder && !!errors.featuredOrder}
                        className="w-full"
                        id="featuredOrder"
                        name="featuredOrder"
                        onChange={(e) => {
                          setFieldValue("featuredOrder", e.value);
                          setFieldTouched("featuredOrder", true, false);
                        }}
                        onBlur={handleBlur}
                        value={values.featuredOrder}
                        options={getAvailableFeaturedOrders()}
                        optionLabel="label"
                        optionValue="value"
                        disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                        emptyMessage="No hay posiciones disponibles"
                      />
                      <label htmlFor="featuredOrder">Orden (1-5)</label>
                    </span>
                    {touched.featuredOrder && errors.featuredOrder && (
                      <small className="p-error">{errors.featuredOrder}</small>
                    )}
                  </div>
                  <div className="col-12 md:col-9 my-2 flex align-items-center">
                    {(() => {
                      const availableOrders = getAvailableFeaturedOrders();
                      const isFull = availableOrders.length === 0;

                      return (
                        <small className={isFull ? "p-error" : "text-color-secondary"}>
                          {isFull
                            ? `No hay lugares disponibles para ${featuredTenantLabel}. Ya existen 5 eventos destacados.`
                            : `Posiciones disponibles para ${featuredTenantLabel}: ${availableOrders.map(o => o.value).join(", ")}`}
                        </small>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            <div className="col-12 md:col-6 my-2">
              <span className="p-float-label">
                <InputText
                  className="w-full"
                  id="price"
                  name="price"
                  prefix="$ "
                  keyfilter="num"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.price}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="price">Costo INTERNO total del evento</label>
              </span>
            </div>
            <div className="col-12 md:col-6 my-2">
              <span className="p-float-label">
                <InputText
                  invalid={!!touched.inscription && !!errors.inscription}
                  className="w-full"
                  id="inscription"
                  name="inscription"
                  prefix="$ "
                  keyfilter="num"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.inscription}
                  disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                />
                <label htmlFor="inscription">Valor de Inscripción</label>
              </span>
              {touched.inscription && errors.inscription && (
                <small className="p-error">{errors.inscription}</small>
              )}
            </div>
            <div className="col-12 my-2 flex flex-column align-items-center">
              <div className="flex justify-content-center align-items-center gap-4">
                <div className="flex align-items-center gap-2">
                  <label htmlFor="active">Activo</label>
                  <InputSwitch
                    id="active"
                    checked={values.active}
                    onChange={(e) => setFieldValue("active", e.value)}
                    disabled={
                      isLoadingCreate ||
                      isLoadingUpdate ||
                      isLoadingRecurrent ||
                      (!!eventData && entity != null && !entity.active && !!entity.deactivatedAt)
                    }
                  />
                </div>
                {/* hide recurrence switch if creating a new featured event */}
                {(!values.featured || !!eventData) && (
                  <div className="flex align-items-center gap-2">
                    <label htmlFor="isRecurring">Evento recurrente</label>
                    <InputSwitch
                      id="isRecurring"
                      checked={!!values.isRecurring}
                      onChange={(e) => setFieldValue("isRecurring", e.value)}
                      disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent || (!eventData && values.featured) || isEditingRecurrentGroup}
                    />
                  </div>
                )}
              </div>
              {!!eventData && entity != null && !entity.active && !!entity.deactivatedAt && (
                <small className="block mt-1 text-500">
                  Un evento desactivado no puede volver a activarse.
                </small>
              )}
            </div>

            {
              values.isRecurring && (
                <div className="col-12">
                  <RecurrenceFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    isDisabled={!!(isLoadingCreate || isLoadingUpdate || isLoadingRecurrent || isEditingRecurrentGroup)}
                  />
                </div>
              )
            }

            {showAdvancedConfig && (
              <div className="col-12 my-2">
                <Panel
                  toggleable
                  collapsed
                  pt={{ toggler: { "aria-label": "Supresión de notificaciones y valoración" } }}
                  header={
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <span>Supresión de notificaciones y valoración</span>
                      {isSuppressionActive && (
                        <Tag
                          severity="warning"
                          icon="pi pi-exclamation-triangle"
                          value="Supresión activa"
                        />
                      )}
                    </div>
                  }
                >
                  <div className="grid">
                    <div className="col-12 md:col-4 my-2">
                      <div className="flex align-items-center gap-2">
                        <InputSwitch
                          id="suppressNotifications"
                          checked={!!values.suppressNotifications}
                          onChange={(e) => setFieldValue("suppressNotifications", e.value)}
                          disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                        />
                        <label htmlFor="suppressNotifications">
                          Suprimir notificaciones push/email
                        </label>
                      </div>
                      <small className="text-color-secondary block mt-1">
                        Los inscriptos no reciben push ni emails de este evento.
                      </small>
                    </div>
                    <div className="col-12 md:col-4 my-2">
                      <div className="flex align-items-center gap-2">
                        <InputSwitch
                          id="suppressRatingPrompt"
                          checked={!!values.suppressRatingPrompt}
                          onChange={(e) => setFieldValue("suppressRatingPrompt", e.value)}
                          disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                        />
                        <label htmlFor="suppressRatingPrompt">
                          Suprimir prompt de valoración
                        </label>
                      </div>
                      <small className="text-color-secondary block mt-1">
                        Al finalizar, no se pide a los asistentes que valoren el evento.
                      </small>
                    </div>
                    <div className="col-12 md:col-4 my-2">
                      <div className="flex align-items-center gap-2">
                        <span className="p-float-label flex-grow-1">
                          <InputNumber
                            inputId="recurrenceSignupWeeks"
                            invalid={
                              !!touched.recurrenceSignupWeeks &&
                              !!errors.recurrenceSignupWeeks
                            }
                            className="w-full"
                            value={values.recurrenceSignupWeeks ?? 0}
                            onValueChange={(e) =>
                              setFieldValue("recurrenceSignupWeeks", e.value ?? 0)
                            }
                            onBlur={() => setFieldTouched("recurrenceSignupWeeks", true)}
                            min={0}
                            max={12}
                            maxFractionDigits={0}
                            useGrouping={false}
                            showButtons
                            disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                          />
                          <label htmlFor="recurrenceSignupWeeks">
                            Semanas de pre-inscripción
                          </label>
                        </span>
                      </div>
                      <small className="text-color-secondary block mt-1">
                        Semanas hacia adelante en las que los usuarios pueden inscribirse a varias fechas de la serie. 0 = desactivado.
                      </small>
                      {touched.recurrenceSignupWeeks && errors.recurrenceSignupWeeks && (
                        <small className="p-error">{errors.recurrenceSignupWeeks}</small>
                      )}
                    </div>
                  </div>
                </Panel>
              </div>
            )}
          </div>
          </fieldset>
        </FormSection>

        {/* Antes de Asistencia y con key propia: insertarla más arriba cuando terminan de
            cargar las reglas correría las keys posicionales y remontaría las secciones. */}
        {showBonusSection && (
          <FormSection key="event-bonus-points" title="Puntos">
            <fieldset {...eventFieldsetProps}>
            <div className="grid">
              {!!eventData && !!entity?.hasCheckins && (
                <div className="col-12">
                  <Message
                    severity="info"
                    text={EVENT_BONUS_CHECKINS_NOTICE}
                    className="w-full"
                  />
                </div>
              )}
              <div className="col-12 field">
                <div className="flex align-items-center gap-3">
                  <InputSwitch
                    id="bonusEnabled"
                    checked={!!values.bonusEnabled}
                    onChange={(e) => {
                      setFieldValue("bonusEnabled", !!e.value);
                      if (!e.value) setFieldTouched("bonusPoints", false, false);
                    }}
                    disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                  />
                  <label htmlFor="bonusEnabled" className="mb-0">
                    Este evento otorga puntos extra
                  </label>
                </div>
                <small className="text-color-secondary block mt-1">
                  Los asistentes suman estos puntos a los de la asistencia cuando se confirma su check-in.
                </small>
              </div>

              {values.bonusEnabled && (
                <>
                  <div className="col-12 md:col-4 field">
                    <label htmlFor="bonusPoints">Puntos extra *</label>
                    <InputNumber
                      inputId="bonusPoints"
                      placeholder="Ej: 20"
                      className="w-full"
                      invalid={!!touched.bonusPoints && !!errors.bonusPoints}
                      value={values.bonusPoints ?? null}
                      // onChange y no onValueChange: este último recién se dispara al salir del
                      // campo, y la vista previa de la card tiene que seguir lo que se tipea.
                      onChange={(e) => setFieldValue("bonusPoints", e.value ?? null)}
                      onBlur={() => setFieldTouched("bonusPoints", true)}
                      maxFractionDigits={0}
                      useGrouping={false}
                      disabled={isLoadingCreate || isLoadingUpdate || isLoadingRecurrent}
                    />
                    <small className="text-color-secondary block mt-1">
                      {eventBonusMaxPoints !== null
                        ? `Número entero mayor a 0. Tope del tenant: ${formatPointRewardCost(eventBonusMaxPoints)}.`
                        : "Número entero mayor a 0."}
                    </small>
                    {touched.bonusPoints && errors.bonusPoints && (
                      <small className="p-error block">{errors.bonusPoints}</small>
                    )}
                  </div>
                  <div className="col-12 md:col-8 field">
                    <span className="block mb-2">Así se ve en la card de la app</span>
                    <EventBonusCardPreview
                      eventName={values.name}
                      bonusPoints={values.bonusPoints ?? null}
                      currencyPlural={appCurrencyName.plural}
                    />
                  </div>
                </>
              )}
            </div>
            </fieldset>
          </FormSection>
        )}

        <AttendanceConfigSection
          key={`${entity?.id ?? "new"}-${values.tenantId}`}
          tenantId={values.tenantId || null}
          eventId={entity?.id ?? null}
          onDraftChange={setAttendanceDraft}
          onReadyChange={setIsAttendanceConfigReady}
          disabled={isSubmitLoading}
        />

      </FormSections>

      {submitValidationMessage && (
        <Message severity="error" text={submitValidationMessage} className="w-full mt-3" />
      )}
      <FormActions
        primaryLabel={attendanceConfigSave.pendingEventIds.length > 0 ? "Reintentar configuración" : eventData ? "Guardar cambios" : "Crear Evento"}
        disabled={isSubmitBlocked || Boolean(attendanceValidationMessage) || (!eventData && !tenantGate.allowed)}
        disabledReason={attendanceValidationMessage || (!eventData && !tenantGate.allowed ? tenantGate.reason : undefined)}
        loading={isSubmitLoading}
        onCancel={goBack}
      />

      <EventUpdateScopeDialog
        visible={showUpdateScopeModal}
        onHide={() => setShowUpdateScopeModal(false)}
        onSubmit={(updateAll) => handleUpdateScope(updateAll)}
      />

      <HostSpotUnavailableDialog
        visible={showHostSpotDialog}
        onHide={() => setShowHostSpotDialog(false)}
        occupiedSpots={occupiedSpots ?? 0}
        totalSpots={Number(values.amount || 0)}
        registrationCheckFailed={hostRegistrationCheckFailed}
      />
    </form >
  );


};
